import { QuickLink, QuickLinkType } from '../models/quick-link';

export class QuickLinkUtils {

    /**
     * Gets the ESquirrel QuickLink Regex.
     */
    static getESquirrelRegex(): RegExp {
        return /(.*)eSqu?irrel(.*)/gi;
    }

    /**
     * Gets the GMT QuickLink Regex.
     */
    static getGMTRegex(prefix: string): RegExp {
        return new RegExp(`${prefix}(g)?mt[\\\d]{1,3}$`, 'gi');
    }

    static getPhaseRegex(prefix: string): RegExp {
        return new RegExp(`(${prefix})?Phase(6)?(.*)$`, 'gi');
    }

    static getYedoRegex(): RegExp {
        return new RegExp('https\:\/\/(www\.)?yedo\.at(.*)$', 'gi');
    }

    static getIntroRegex(): RegExp {
        return /intro-(.*)+$/gi;
    }

    static getVideoRegex(): RegExp {
        return /video$/;
    }

    /**
     * Example see: https://digi.schule/digibuecher/books/genial-mathematik-4-schulbuch-ikt/2262?p=50
     */
    static getMultipleExercisesRegex(): RegExp {
        return /^digi\.schule.*\D$/;
    }

    /**
     * Checks if a quicklink is an HSM link
     */
    static isHSMLink(quickLink: QuickLink, quickLinkPrefix: string = null): boolean {
        const prefix = (quickLinkPrefix || '').toLowerCase();
        const quickLinkLink = quickLink.link || '';
        const prefixIndex = quickLinkLink.toLowerCase().indexOf(prefix);
        const prefixEndIndex = prefixIndex > 0 ? prefixIndex + prefix.length : 0;
        const quickLinkWithoutPrefix = quickLinkLink.substring(prefixEndIndex).toLowerCase();

        const isHsmPattern = this.checkHSMPattern(quickLinkWithoutPrefix);

        return (quickLink.type === 2 || quickLink.type === 3) && isHsmPattern;
    }

    static isHSMLinkPattern(link: string): boolean {
        return this.checkHSMPattern(link);
    }

    /**
     * Checks if a  QuickLink is signable or not
     * @param {QuickLink} quickLink
     * @param prefix
     * @returns {boolean}
     */
    static isSignable(quickLink: QuickLink, prefix: string): boolean {
        let code = quickLink.link.split('/').pop();
        if (code) {
            code = code.trim();
        }

        return this.linkMatchesSignableQuickLink(code, prefix);
    }

    /**
     * Extracts the link code from the link.
     * @param {string} link
     * @returns {string}
     */
    static getLinkCode(link: string): string {

        if (!link) {
            return '';
        }

        let code = link.split('/').pop();

        if (code) {
            code = code.trim();
        }

        return code;
    }

    /**
     * Gets the QuickLinkType of a Quicklink code.
     * @param {string} quickLink
     * @param prefix
     * @returns {QuickLinkType}
     */
    static getMatchingQuickLinkType(quickLink: string, prefix: string): QuickLinkType {
        const code = QuickLinkUtils.getLinkCode(quickLink);
        if (code.match(QuickLinkUtils.getESquirrelRegex()) !== null) {
            return QuickLinkType.E_SQUIRREL;
        }

        if (code.match(QuickLinkUtils.getGMTRegex(prefix)) !== null) {
            return QuickLinkType.GMT;
        }

        if (code.match(QuickLinkUtils.getPhaseRegex(prefix)) !== null) {
            return QuickLinkType.PHASE;
        }

        // Yedo special case. Quick Link Redirects are used. No "code" within the quickLink.
        if (code.match(QuickLinkUtils.getYedoRegex()) !== null || quickLink.match(QuickLinkUtils.getYedoRegex()) !== null) {
            return QuickLinkType.YEDO;
        }

        if (this.checkHSMPattern(code)) {
            return QuickLinkType.HSM;
        }

        return QuickLinkType.OTHER;
    }

    static isQuicklinkLeadingToAPage(code: string, prefix = ''): boolean {

        if (prefix) {
            // Regex: 's' followed by a digit
            // eslint-disable-next-line max-len
            return code.includes(prefix) ? /(s)(\d+)$/gim.test(code.substr(code.indexOf(prefix) + prefix.length, code.length)) : /(.*)(s)(\d+)$/gim.test(code);
        }

        // Regex: anything followed by 's' followed by a digit
        return /(.*)(s)(\d+)$/gim.test(code);
    }

    static checkQuickLinkForSolution(quickLink: QuickLink, prefix = ''): boolean {

        if (quickLink.type !== 3) {
            return false;
        }

        if (quickLink.link.match(QuickLinkUtils.getIntroRegex()) ||
            quickLink.link.match(QuickLinkUtils.getVideoRegex()) ||
            this.isQuicklinkLeadingToAPage(quickLink.link, prefix) ||
            this.isHSMLink(quickLink, prefix) ||
            quickLink.link.match(QuickLinkUtils.getMultipleExercisesRegex())) {
            return false;
        }

        return true;
    }

    private static checkHSMPattern(quickLinkWithoutPrefix: string): boolean {
        return quickLinkWithoutPrefix.includes('hsm') ||
            quickLinkWithoutPrefix.includes('am') ||
            /^[a-zA-Z0-9\/\-_.,]+(ci|co)[a-zA-Z0-9\/\-_.,]+[mw]$/.test(quickLinkWithoutPrefix);
    }

    /**
     * Checks if a QuickLink is a signable Quicklink
     * @param {string} code
     * @param prefix
     * @returns {boolean}
     */
    private static linkMatchesSignableQuickLink(code: string, prefix: string): boolean {
        const type = QuickLinkUtils.getMatchingQuickLinkType(code, prefix);

        return type === QuickLinkType.E_SQUIRREL || type === QuickLinkType.PHASE || type === QuickLinkType.YEDO;
        // QuickLinkUtils.getMatchingQuickLinkType(code) === QuickLinkType.GMT; GMT api not ready
    }
}
